import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, model, OnInit, Output, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IncidentCodeService } from '@shared/services/incident-code-service';
import { ProductionTerminalService } from '@shared/services/production-terminal.service';
import { ICompletionFlow, ICompletionGroupType, IPostTerminalRequest, ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { ITerminalTemplate, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { ListPrintConfigurationComponent } from '../../mes-components/list-print-configuration/list-print-configuration.component';

@Component({
	selector: 'app-create-terminal',
	templateUrl: './create-terminal.component.html',
	styleUrls: ['./create-terminal.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateTerminalComponent implements OnInit, AfterViewInit {
	@Output() created: EventEmitter<any> = new EventEmitter();
	@ViewChild(ListPrintConfigurationComponent)
	printConfigurationListComponent: ListPrintConfigurationComponent;

	form: FormGroup;
	loading: boolean = false;
	terminal: IPostTerminalRequest = {
		alias: null,
		name: null,
		completionFlow: null,
		completionGroupType: ICompletionGroupType.Groups,
		stationFilter: '1',
		terminalTemplateId: '',
		workTaskId: '',
		sqlQuery: '',
		workTaskCategories: [],
		incidentCodeGroups: [],
		nextId: null,
		printConfigurations: [],
	};

	completionFlows: string[] = Object.keys(ICompletionFlow).filter((key) => Number.isNaN(Number(ICompletionFlow[key])));

	completionGroupTypes: string[] = Object.keys(ICompletionGroupType).filter((key) => Number.isNaN(Number(ICompletionGroupType[key])));

	httpError: string = '';

	terminalTemplates: ITerminalTemplate[] = [];
	workTasks: IWorktask[] = [];
	terminals: ITerminal[] = [];

	readonly separatorKeysCodes: number[] = [ENTER, COMMA];
	categories: string[];
	selectedCategories = signal([]);
	currentCategory = model('');
	uncategorizedLabel: string;
	incidentCodeGroups: string[];

	constructor(
		public dialogRef: MatDialogRef<CreateTerminalComponent>,
		private terminalService: TerminalAdministrationService,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private workTaskService: WorktaskService,
		private incidentCodeService: IncidentCodeService,
		private translateService: TranslateService,
		private productionTerminalService: ProductionTerminalService,
		private fb: FormBuilder,
	) {}

	ngAfterViewInit(): void {
		this.form.addControl('printConfigurations', this.printConfigurationListComponent.form);
	}

	ngOnInit(): void {
		this.translateService.get('mes.mes-production-terminal.uncategorized-worktask-label').subscribe((label: string) => {
			this.uncategorizedLabel = label;
		});
		this.initializeForm();
		this.populateForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.terminal.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.terminal.name, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			completionFlow: [this.terminal.completionFlow, [Validators.required]],
			completionGroupType: [this.terminal.completionGroupType, [Validators.required]],
			stationFilter: [this.terminal.stationFilter, [Validators.maxLength(2), Validators.required, Validators.pattern(ValidatorPatterns.WholeNumber)]],
			terminalTemplateId: [this.terminal.terminalTemplateId, [Validators.required, Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			workTaskId: [this.terminal.workTaskId, [Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			nextId: [this.terminal.nextId, [Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			sqlQuery: [this.terminal.sqlQuery, [Validators.maxLength(200000)]],
			workTaskCategories: [[]],
			incidentCodeGroups: [[]],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.terminal) {
					if (key === 'workTaskCategories') {
						this.terminal.workTaskCategories = value[key].map((item) => (item === this.uncategorizedLabel ? '' : item));
					} else if (key === 'incidentCodeGroups') {
						this.terminal.incidentCodeGroups = value[key];
					} else {
						this.terminal[key] = value[key];
					}
				}
			});
		});
	}

	populateForm(): void {
		this.terminalTemplateService.getTerminalTemplates().subscribe((response) => {
			this.terminalTemplates = response.result.sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		});
		this.workTaskService.getWorktasks().subscribe((response) => {
			this.workTasks = response.result.filter((c) => c.isActive).sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
			this.categories = this.workTasks
				.map((c) => (c.tags['categoryName'] ? c.tags['categoryName'] : this.uncategorizedLabel))
				.filter((value, index, self) => self.indexOf(value) === index)
				.sort();
		});
		this.terminalService.getTerminals().subscribe((response) => {
			this.terminals = response.result.sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		});
		this.incidentCodeService.getAll().subscribe((incidentCodes) => {
			const groupedCodes = this.incidentCodeService.groupIncidentCodes(incidentCodes);
			this.incidentCodeGroups = groupedCodes.map((g) => g.group).sort();
		});
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	onCancel = () => this.dialogRef.close();

	onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;
			this.terminal.printConfigurations = this.printConfigurationListComponent.getPayload();
			this.terminalService.createTerminal(this.terminal).subscribe({
				next: (createdTerminal) => {
					this.productionTerminalService.applyTerminalUpdate(createdTerminal);
					this.created.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
		}
	}
}
